import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../CartContext';
import axios from 'axios';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const API = process.env.REACT_APP_API_URL;

function PaymentForm() {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/order-confirmation` },
    });
    if (error) { setError(error.message); setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && <p style={{ color: 'red', marginTop: '12px' }}>{error}</p>}
      <button type="submit" disabled={!stripe || loading}
        style={{ marginTop: '20px', width: '100%', padding: '14px',
          background: '#111', color: '#fff', border: 'none',
          borderRadius: '8px', fontSize: '16px',
          cursor: loading ? 'not-allowed' : 'pointer' }}>
        {loading ? 'Processing...' : 'Pay Now'}
      </button>
    </form>
  );
}

export default function Checkout() {
  const { cart, removeFromCart, updateQuantity, cartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    customer_email: '', shipping_name: '',
    shipping_address: '', wallet_address: ''
  });

  const inputStyle = {
    width: '100%', padding: '10px 12px', marginBottom: '12px',
    border: '1px solid #ddd', borderRadius: '6px',
    fontSize: '15px', boxSizing: 'border-box',
  };

  const handleProceedToPayment = async () => {
    if (!form.customer_email || !form.shipping_name || !form.shipping_address) {
      alert('Please fill in your email, name and shipping address.');
      return;
    }
    if (cart.length === 0) {
      alert('Your basket is empty.');
      return;
    }
    try {
      const res = await axios.post(`${API}/create-payment-intent`, {
        items: cart.map(i => ({
          product_id: i.product.id,
          quantity: i.quantity,
          personalisation_text: i.personalisationText || null,
          preset_design_id: i.presetDesignId || null,
        })),
        customer_email: form.customer_email,
        shipping_name: form.shipping_name,
        shipping_address: form.shipping_address,
        wallet_address: form.wallet_address || null,
      });
      setClientSecret(res.data.client_secret);
      setStep(2);
    } catch (err) {
      alert('Error creating payment. Please try again.');
    }
  };

  if (cart.length === 0 && step === 1) return (
    <div style={{ padding: '60px', textAlign: 'center', fontFamily: 'sans-serif' }}>
      <p style={{ fontSize: '48px' }}>🛒</p>
      <h2>Your basket is empty</h2>
      <button onClick={() => navigate('/')}
        style={{ marginTop: '16px', padding: '12px 32px', background: '#111',
          color: '#fff', border: 'none', borderRadius: '8px',
          fontSize: '16px', cursor: 'pointer' }}>
        Browse Products
      </button>
    </div>
  );

  return (
    <div style={{ fontFamily: 'sans-serif', maxWidth: '700px', margin: '0 auto', padding: '32px 20px' }}>
      <h2>Checkout</h2>

      {step === 1 && (
        <>
          <h3>Your Basket</h3>
          {cart.map((item, index) => (
            <div key={index} style={{
              display: 'flex', alignItems: 'center', gap: '16px',
              padding: '16px', border: '1px solid #eee',
              borderRadius: '8px', marginBottom: '12px',
            }}>
              {item.product.image_url
                ? <img src={item.product.image_url} alt={item.product.name}
                    style={{ width: '70px', height: '70px', objectFit: 'cover', borderRadius: '6px' }} />
                : <div style={{ width: '70px', height: '70px', background: '#f5f5f5',
                    borderRadius: '6px', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: '28px' }}>🔑</div>
              }
              <div style={{ flex: 1 }}>
                <p style={{ margin: '0 0 4px', fontWeight: 'bold' }}>{item.product.name}</p>
                {item.presetDesignId && (
                  <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#666' }}>
                    Design selected
                  </p>
                )}
                <p style={{ margin: 0, color: '#333' }}>
                  £{item.product.base_price.toFixed(2)} each
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button onClick={() => updateQuantity(index, item.quantity - 1)}
                  style={{ width: '28px', height: '28px', border: '1px solid #ddd',
                    borderRadius: '4px', cursor: 'pointer', background: '#f5f5f5' }}>−</button>
                <span>{item.quantity}</span>
                <button onClick={() => updateQuantity(index, item.quantity + 1)}
                  style={{ width: '28px', height: '28px', border: '1px solid #ddd',
                    borderRadius: '4px', cursor: 'pointer', background: '#f5f5f5' }}>+</button>
              </div>
              <div style={{ textAlign: 'right', minWidth: '70px' }}>
                <p style={{ margin: '0 0 4px', fontWeight: 'bold' }}>
                  £{(item.product.base_price * item.quantity).toFixed(2)}
                </p>
                <button onClick={() => removeFromCart(index)}
                  style={{ background: 'none', border: 'none', color: '#e53',
                    cursor: 'pointer', fontSize: '13px' }}>Remove</button>
              </div>
            </div>
          ))}

          <div style={{ textAlign: 'right', fontSize: '20px', fontWeight: 'bold',
            padding: '16px 0', borderTop: '2px solid #111', marginBottom: '24px' }}>
            Total: £{cartTotal.toFixed(2)}
          </div>

          <h3>Your Details</h3>
          <input placeholder="Email address *" style={inputStyle} value={form.customer_email}
            onChange={e => setForm({ ...form, customer_email: e.target.value })} />
          <input placeholder="Full name *" style={inputStyle} value={form.shipping_name}
            onChange={e => setForm({ ...form, shipping_name: e.target.value })} />
          <textarea placeholder="Shipping address *"
            style={{ ...inputStyle, height: '90px', resize: 'vertical' }}
            value={form.shipping_address}
            onChange={e => setForm({ ...form, shipping_address: e.target.value })} />
          <input placeholder="Crypto wallet address (optional — for NFT certificate)"
            style={inputStyle} value={form.wallet_address}
            onChange={e => setForm({ ...form, wallet_address: e.target.value })} />

          <button onClick={handleProceedToPayment}
            style={{ width: '100%', padding: '14px', background: '#111', color: '#fff',
              border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'pointer' }}>
            Continue to Payment →
          </button>
        </>
      )}

      {step === 2 && clientSecret && (
        <>
          <h3>Payment — £{cartTotal.toFixed(2)}</h3>
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentForm />
          </Elements>
        </>
      )}
    </div>
  );
}
